import cx from 'classnames';
import { parseISO } from 'date-fns';
import { Fragment } from 'react';
import { Icon } from '@components/icon/Icon';
import { ModalContainer } from '@components/modal/ModalContainer';
import {
  parseToShortDateFormat,
  toISODateFormat,
  toShortDayFormat,
} from '@shared/utils/dateFormatters';
import { useQuery } from '@tanstack/react-query';
import { ISOTimeTo12HourTime } from '@utils/time';
import typography from '~styles/typography.scss';
import { getFloorPlanTableAvailabilities } from './apiHelpers';
import styles from './UnavailableTableModal.scss';
import { FloorPlanIcons } from '@components/icon/FloorPlanIcons';

interface UnavailableTableModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date;
  restaurantId: string;
  floorPlanTable: {
    id: string;
    iconName: string;
  };
}

export const UnavailableTableModal = ({
  onClose,
  selectedDate,
  restaurantId,
  floorPlanTable,
}: UnavailableTableModalProps) => {
  const fetchAvailabilities = () =>
    getFloorPlanTableAvailabilities(
      restaurantId,
      floorPlanTable.id,
      toISODateFormat(selectedDate),
    );
  const { data, isFetching } = useQuery({
    queryKey: ['floorPlanTableAvailabilities', floorPlanTable.id],
    queryFn: fetchAvailabilities,
    throwOnError: true,
  });

  if (isFetching) return null;
  if (!data) return null;

  const floorPlanTableDates = Object.keys(data);
  const firstDayDate = floorPlanTableDates[0];
  const lastDayDate = floorPlanTableDates[floorPlanTableDates.length - 1];

  return (
    <ModalContainer disablePortal={false} isOpen onClose={onClose}>
      <div aria-labelledby="dialogTitle" className={styles.modal} role="dialog">
        <button
          className={styles.closeButton}
          aria-label="Close modal"
          onClick={onClose}
          type="button"
        >
          <Icon name="close" />
        </button>
        <header>
          <div>
            <FloorPlanIcons
              className={styles.tableIcon}
              name={floorPlanTable.iconName}
            />
            <div className={styles.title}>
              <h1 id="dialogTitle" className={typography.t3}>
                Availabilities
              </h1>
              <p
                className={typography.d5}
              >{`${parseToShortDateFormat(firstDayDate, false)} - ${parseToShortDateFormat(lastDayDate, false)}`}</p>
            </div>
          </div>
        </header>
        <dl className={styles.floorPlanTableAvailabilitiesGrid}>
          {Object.entries(data).map(([dateISO, availabilities]) => {
            const availabilityCount = availabilities.length;
            return (
              <Fragment key={dateISO}>
                <AvailabilityDay
                  date={dateISO}
                  availabilityCount={availabilityCount}
                  isSelected={dateISO === toISODateFormat(selectedDate)}
                />
                {availabilityCount ? (
                  availabilities.map((listing) => (
                    <Availability key={JSON.stringify(listing)} {...listing} />
                  ))
                ) : (
                  <NoAvailability />
                )}
              </Fragment>
            );
          })}
        </dl>
      </div>
    </ModalContainer>
  );
};

interface AvailabilityDayProps {
  availabilityCount: number;
  date: string;
  isSelected: boolean;
}

const AvailabilityDay = ({
  availabilityCount,
  date,
  isSelected,
}: AvailabilityDayProps) => {
  const spanCount = 1 + availabilityCount > 1 ? availabilityCount : 0;
  return (
    <dt
      className={cx(typography.c3, styles.dayGroup, {
        [styles.white]: !availabilityCount,
      })}
      style={{
        gridRow: `span ${spanCount}`,
      }}
    >
      {isSelected && <div className={styles.goldDot} />}
      {`${toShortDayFormat(parseISO(date))}:`}
    </dt>
  );
};

interface AvailabilityProps {
  startTime: string;
  endTime: string;
  minGuest: number;
  maxGuest: number;
}

const Availability = ({
  startTime,
  endTime,
  minGuest,
  maxGuest,
}: AvailabilityProps) => {
  const timeRange = `${ISOTimeTo12HourTime(startTime)} - ${ISOTimeTo12HourTime(endTime)}`;
  const guestRange = `${minGuest}-${maxGuest}`;

  return (
    <>
      <dd className={typography.c3}>{timeRange}</dd>
      <dd className={typography.c3}>
        <Icon name="couple" />
        {guestRange}
      </dd>
    </>
  );
};

const NoAvailability = () => (
  <dd
    className={cx(typography.c3, styles.white)}
    style={{ gridColumn: 'span 2' }}
  >
    Not Available
  </dd>
);
